<template>
  <ChartLayout :title="title">
    <DataGetter
      :url="apiRoute"
      :params="{
        countryISO: countryISO,
        indicator: 'Total',
        topic: 'Patents',
        techSector: ['Fossil fuel patents', 'Clean energy patents'],
        startYear: 2000,
        allYears: true,
      }"
      v-slot="{ data }"
      lazy
    >
      <DataSlicer
        :key="`${countryISO}patentbyyearslicer`"
        :data="data"
        x-axis-dimension="year"
        y-axis-dimension="techSector"
        value-dimension="value"
        :data-formatter="scaleToBase"
        v-slot="{ slicedData }"
      >
        <Chart
          :options="{
            ...chartOptions,
            series: slicedData.map((series) =>
              series.name === 'Share of clean energy patents'
                ? {
                    ...series,
                    type: 'line',
                    yAxis: 1,
                    tooltip: { valueSuffix: '%', valueDecimals: 0 },
                  }
                : series
            ),
          }"
        />
      </DataSlicer>
    </DataGetter>
    <template #notes>
      The datapoints for 2022 and 2023 are considered provisional due to the time lag in filing applications in the PATSTAT database. 
      Only the countries having more 30 clean energy patents over the time
      period in the database can be selected on this page. For data on all
      countries you can download from the full data set at the top of the page.
    </template>
  </ChartLayout>
</template>

<script>
import { Chart } from "highcharts-vue";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
import { DataGetter, DataSlicer } from "@international-energy-agency/vue-ui";
export default {
  props: {
    title: {
      type: String,
      required: false,
    },
    apiRoute: {
      type: String,
      default: "https://api.iea.org/h2020/",
    },
    countryISO: {
      type: String,
      required: true,
    },
    provisionalYear: {
      type: Number,
      default: 2022
    }
  },
  components: {
    Chart,
    ChartLayout,
    DataGetter,
    DataSlicer,
  },
  computed: {
    chartOptions() {
      const { provisionalYear } = this;
      return ChartOptions.buildOptions(
        {
          plotOptions: {
            column: {
              stacking: false,
            },
             line: {
              zoneAxis: 'x',
              zones: [
                {
                  value: provisionalYear - 0.5,
                  className: ''
                },
                {
                  className: 'highcharts-dotted'
                }
              ]
            }
          },
          xAxis: {
            showFirstLabel: true,
            showLastLabel: true,
            plotLines: [
                { value: provisionalYear - 0.5, className: "highcharts-dotted", zIndex: 2 },
              ]
          },
          yAxis: [
            {
              title: {
                text: "Index (2000 = 100)",
              },
              min: 0,
              floor: 0,
              tickAmount: 6,
              plotLines: [
                { value: 0, className: "highcharts-zero-axis", zIndex: 1 },
              ],
              className: "highcharts-show-grid",
            },
            /*
            {
              title: {
                text: "Share of clean energy patents",
              },
              allowDecimals: false,
              opposite: true,
              tickAmount: 6,
              min: 0,
              floor: 0,
              max: 100,
              labels: {
                format: "{text}%",
              },
            },
            */
          ]
        },
        "line"
      );
    },
  },
  methods: {
    // Scale all amounts to base year
    scaleToBase(data) {
      const baseAmount = {};
      return data.reduce((scaled, row) => {
        if (!baseAmount[row.techSector]) {
          baseAmount[row.techSector] = row.value;
          scaled.push({ ...row, value: 100 });
        } else {
          scaled.push({
            ...row,
            value: (row.value / baseAmount[row.techSector]) * 100,
          });
        }
        return scaled;
      }, []);
    },
    // Add a series for share (NOT USED currently)
    formatData(data) {
      const cleanEnergy = data.filter(
        (row) => row.techSector === "Clean energy patents"
      );
      const fossilEnergy = data.filter(
        (row) => row.techSector === "Fossil fuel patents"
      );
      const share = cleanEnergy.map((row) => {
        const comparison = fossilEnergy.find((r) => r.year === row.year);
        return {
          ...row,
          techSector: "Share of clean energy patents",
          value: !comparison
            ? null
            : (row.value / (comparison.value + row.value)) * 100,
        };
      });
      return [...cleanEnergy, ...fossilEnergy, ...share];
    },
  },
};
</script>
